<template>
  <div v-if="!isMobile" class="w-full bg-white overflow-x-hidden">
    <!-- Hero Section -->
    <section class="h-[50vh] relative bg-gradient-to-r from-[#0D0971] to-[#1a1a8e] bg-cover bg-center">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 h-full flex flex-col justify-center items-center text-center px-[9%]">
        <h1 class="font-bold text-[3.5vw] text-white mb-[1vw]">
          {{ categoryData?.title || 'Category Details' }}
        </h1>
        <div class="w-[20vw] h-[0.3vw] bg-white/50 mb-[2vw]"></div>
        <button
          @click="goBack"
          class="mt-[2vw] px-[2vw] py-[0.8vw] bg-white text-[#0D0971] font-semibold text-[1.1vw] rounded-lg hover:bg-gray-100 transition-colors duration-300 flex items-center gap-[0.5vw]">
          <svg class="w-[1.2vw] h-[1.2vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>

    <!-- Content Section -->
    <section class="pt-[4vw] pb-[0.5vw]">
      <div class="w-[81vw] mx-auto">
        <!-- Description -->
        <div class="mb-[3vw]">
          <div v-if="Array.isArray(categoryData?.description)" class="space-y-[1.2vw]">
            <p v-for="(paragraph, index) in categoryData.description"
               :key="index"
               class="text-[1.2vw] text-[#444] leading-[1.8]">
              {{ paragraph }}
            </p>
          </div>
          <p v-else class="text-[1.2vw] text-[#444] leading-[1.8]">
            {{ categoryData?.description }}
          </p>
        </div>

        <!-- Sections -->
        <div v-if="categoryData?.sections && categoryData.sections.length > 0">
          <div v-for="(section, sectionIndex) in categoryData.sections" :key="sectionIndex" class="mb-[3vw]">
            <!-- Section Title -->
            <h2 class="text-[2.8vw] font-bold text-[#0D0971] mb-[2vw] flex items-center gap-[0.8vw]">
              <span class="text-[2vw]">{{ sectionIndex + 1 }} |</span>
              {{ section.title }}
            </h2>

            <!-- Table Display -->
            <div v-if="section.isTable && section.tableData" class="overflow-x-auto">
              <table class="w-full border-collapse">
                <thead>
                  <tr class="bg-[#0D0971] text-white">
                    <th class="text-[1.4vw] font-bold p-[1.5vw] text-left border border-[#0D0971]">
                      {{ $i18n.locale === 'vn' ? 'Tên sản phẩm' : 'Product' }}
                    </th>
                    <th class="text-[1.4vw] font-bold p-[1.5vw] text-left border border-[#0D0971]">
                      {{ $i18n.locale === 'vn' ? 'Giới thiệu sản phẩm' : 'Overview' }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in section.tableData" :key="rowIndex"
                      :class="rowIndex % 2 === 0 ? 'bg-[#f3f8ff]' : 'bg-white'">
                    <td class="text-[1.2vw] font-semibold text-[#0D0971] p-[1.5vw] border border-gray-300 align-top">
                      {{ row.product }}
                    </td>
                    <td class="text-[1.2vw] text-[#444] p-[1.5vw] border border-gray-300 align-top">
                      <div v-if="Array.isArray(row.overview)">
                        <p v-for="(paragraph, pIndex) in row.overview" :key="pIndex" class="mb-[0.5vw] last:mb-0">
                          {{ paragraph }}
                        </p>
                      </div>
                      <p v-else>{{ row.overview }}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Section Items (Original Display) -->
            <div v-else-if="section.items && section.items.length > 0" class="space-y-[2vw]">
              <div v-for="(item, index) in section.items" :key="index"
                   class="bg-gradient-to-br from-[#f3F8FF] to-[#e8f0fe] rounded-xl p-[2vw] border-l-4 border-[#0D0971] hover:shadow-lg transition-shadow duration-300">
                <!-- Item Name -->
                <h3 class="text-[1.6vw] font-bold text-[#0D0971] mb-[1vw]">{{ item.name }}</h3>

                <!-- Item Description -->
                <p class="text-[1.2vw] text-[#444] leading-[1.8]">
                  {{ item.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Image Gallery -->
    <section class="">
      <ImageGallery :imageItems="filtrationAccessoriesGallery" imageHeightClass="max-h-[80vh]" :imagesPerPage="1" columns="1" />
    </section>

    <!-- Category Not Found -->
    <section v-if="!categoryData" class="py-[8vw]">
      <div class="w-[81vw] mx-auto text-center">
        <h2 class="text-[2.5vw] font-bold text-[#0D0971] mb-[2vw]">{{ t('categoryDetail.categoryNotFound') }}</h2>
        <p class="text-[1.3vw] text-[#666] mb-[3vw]">
          {{ t('categoryDetail.categoryNotFoundMessage') }}
        </p>
        <button
          @click="goBack"
          class="px-[2.5vw] py-[1vw] bg-[#0D0971] text-white font-semibold text-[1.2vw] rounded-lg hover:bg-[#1a1a8e] transition-colors duration-300">
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>
  </div>

  <!-- Mobile Version -->
  <MobileCategoryFiltrationAccessoriesDetail v-if="isMobile" />
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { useHead } from '@vueuse/head';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import { useDeviceDetection } from "@/utils/deviceDetection";
import MobileCategoryFiltrationAccessoriesDetail from "../components/MobileCategoryFiltrationAccessoriesDetail.vue";
import ImageGallery from "../components/ImageGallery.vue";
import { filtrationAccessoriesGallery } from "../utils/imageData";
import { getSEO } from '../utils/seoConfig';

const route = useRoute();
const router = useRouter();
const { locale, messages, t } = useI18n();
const { isMobile } = useDeviceDetection();

// Get Filtration Accessories category data from i18n (hardcoded to 'filtration-accessories' category)
const categoryData = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const filtrationCategory = categories.find((cat: any) => cat.id === 'filtration-accessories');
  return filtrationCategory?.detailContent || null;
});

// Get category title for meta
const categoryTitle = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const filtrationCategory = categories.find((cat: any) => cat.id === 'filtration-accessories');
  return filtrationCategory?.text || 'Filtration Accessories Category Details';
});

// 动态多语言 SEO
const seo = computed(() => getSEO(locale.value, 'filtration'));

useHead({
  title: computed(() => seo.value.title),
  meta: [
    { name: 'description', content: computed(() => seo.value.description) },
    { name: 'keywords', content: computed(() => seo.value.keywords) },
    { property: 'og:title', content: computed(() => seo.value.title) },
    { property: 'og:description', content: computed(() => seo.value.description) },
  ],
});

onMounted(() => {
  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});

const goBack = () => {
  router.push('/core-offers/source-product');
  pushToDataLayer('click', {
    category: 'CategoryFiltrationAccessoriesDetail',
    label: 'Back to Source Product',
  });
};

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("CategoryFiltrationAccessoriesDetail");
  pushToDataLayer('click', {
    category: 'CategoryFiltrationAccessoriesDetail',
    label: 'Contact Us',
  });
};

</script>

<style scoped>
.inquery {
  position: fixed;
  right: 1.5vw;
  top: 46%;
  z-index: 100;
  width: 5vw;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.inquery:hover {
  transform: scale(1.1);
}
</style>

